import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { log } from "../lib/log";

const KEY = "sirius_theme";
const ThemeCtx = createContext({ theme: "light", toggle: () => {}, setTheme: () => {} });

export function SiriusThemeProvider({ children, defaultTheme = "light" }) {
  const [theme, setThemeState] = useState(() => {
    try {
      const stored = localStorage.getItem(KEY);
      return stored === "dark" || stored === "light" ? stored : defaultTheme;
    } catch {
      return defaultTheme;
    }
  });

  useEffect(() => {
    document.documentElement.setAttribute("data-sirius", theme);
    try {
      localStorage.setItem(KEY, theme);
    } catch (e) {
      log.warn("[theme] localStorage unavailable:", e?.message);
    }
  }, [theme]);

  const setTheme = useCallback((t) => setThemeState(t === "dark" ? "dark" : "light"), []);
  const toggle = useCallback(() => setThemeState((t) => (t === "dark" ? "light" : "dark")), []);

  // Memoize the context value so consumers don't re-render when an unrelated
  // parent re-renders. `toggle` and `setTheme` are already stable via
  // useCallback; only `theme` flips.
  const ctxValue = useMemo(() => ({ theme, toggle, setTheme }), [theme, toggle, setTheme]);

  return <ThemeCtx.Provider value={ctxValue}>{children}</ThemeCtx.Provider>;
}

export function useSiriusTheme() { return useContext(ThemeCtx); }

export function ThemeToggle({ "data-testid": testId = "theme-toggle" }) {
  const { theme, setTheme } = useSiriusTheme();
  return (
    <div className="srs-theme-toggle" data-testid={testId} role="group" aria-label="Theme">
      <button
        type="button"
        className={`srs-th-opt ${theme === "light" ? "active" : ""}`}
        onClick={() => setTheme("light")}
        aria-label="Dagslys"
        title="Dagslys"
        data-testid={`${testId}-light`}
      >☀</button>
      <button
        type="button"
        className={`srs-th-opt ${theme === "dark" ? "active" : ""}`}
        onClick={() => setTheme("dark")}
        aria-label="Natt"
        title="Natt"
        data-testid={`${testId}-dark`}
      >☾</button>
    </div>
  );
}
